import React, { useState, useEffect } from 'react';
import { Label } from 'app/components/ui/label';
import { Input } from 'app/components/ui/input';
import { Button } from 'app/components/ui/button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createNewPasswordSchema } from 'app/utils/schemas';
import { useResetPasswordSlice } from '../slice';
import { useToast } from 'app/components/ui/use-toast';

interface Props {

}

interface CreateNewPasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

export const CreateNewPassword = () => {
  const { toast } = useToast();
  const { useResetPasswordMutation } = useResetPasswordSlice();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState<string>('');

  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    }
  }, []);

  const { 
    handleSubmit, 
    register, 
    formState: { errors } 
  } = useForm<CreateNewPasswordFormData>({
    resolver: zodResolver(createNewPasswordSchema)
  });

  const onSubmit = (data: CreateNewPasswordFormData) => {
    const payload = {
      password: data.newPassword,
      token
    }
    resetPassword(payload).unwrap().then(() => {
      toast({
        title:  "Password updated",
        description: "Your password has been updated",
        variant: "signinsuccess",
      });
      // Redirect to signin page after successful password reset
      window.location.href = '/login';
    }).catch((error) => { 
      console.log(error);
      toast({
        title: "Error updating password",
        description: error,
      });
    });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 gap-16 justify-between">
      <h2 className="font-serif text-[#5B73F1] text-[2rem] font-bold leading-normal">
        Create New Password
      </h2>
      <div className="space-y-8">
        <div className="">
          <Label
            htmlFor="newPassword"
            className="w-fit text-[#21272A] text-sm font-normal leading-normal mb-4"
          >
            New Password
          </Label>
          <div className="relative">
            <Input
              id="newPassword"
              type={showPassword ? "text" : "password"}
              className="bg-white h-11 p-3 pr-8 border-[#DDE1E6] rounded-lg text-black/80 placeholder:text-[#4D5358] text-base font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200"
              placeholder="Enter new password"
              {...register('newPassword')}
            />
            <img 
              src={showPassword ? "/images/eye.svg" : "/images/eye-crossed.svg"}
              alt={showPassword ? "Show Password" : "Hide Password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
            />
            {errors.newPassword && (
              <p className="text-[#B3171A] text-xs font-normal leading-normal mt-0.5">{errors.newPassword.message}</p>
            )}
          </div>
        </div>
        <div className="">
          <Label
            htmlFor="confirmPassword"
            className="w-fit text-[#21272A] text-sm font-normal leading-normal mb-4"
          >
            Confirm Password
          </Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              className="bg-white h-11 p-3 pr-8 border-[#DDE1E6] rounded-lg text-black/80 placeholder:text-[#4D5358] text-base font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200"
              placeholder="Re-enter new password"
              {...register('confirmPassword')}
            />
            <img 
              src={showConfirmPassword ? "/images/eye.svg" : "/images/eye-crossed.svg"}
              alt={showConfirmPassword ? "Show Password" : "Hide Password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            />
            {errors.confirmPassword && (
              <p className="text-[#B3171A] text-xs font-normal leading-normal mt-0.5">{errors.confirmPassword.message}</p>
            )}
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="primary"
          className="h-[3.5rem] py-4 text-xl leading-[1.5rem]"
          disabled={isLoading}
        >
          {isLoading ? "Updating..." : "Update Password"}
        </Button>
      </div>
    </form>
  );
};
